import React, { useEffect, useState } from 'react';
import { Stack, Typography, Button } from '@mui/material';
import { Product } from '@domain/product/models/Product';
import { JSEditableInput } from '@shared/ui';
import { useProductUpdate } from '../../hooks/useProductUpdate';
import { useCustomFields } from '../../hooks/useCustomFields';

interface ProductFieldsProps {
  product: Product;
}

const ProductFields: React.FC<ProductFieldsProps> = ({
  product,
}) => {
  const [ean, setEan] = useState(product.ean);
  const [sku, setSku] = useState(product.sku);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>(
    product.customFields ?? {}
  );

  const { updateProduct } = useProductUpdate();
  const { customFields: customFieldsResponse, createCustomField, updateCustomField } = useCustomFields();
  const companyCustomFields = customFieldsResponse?.status === 'success'
    ? customFieldsResponse.data
    : [];

  useEffect(() => {
    setEan(product.ean);
    setSku(product.sku);
    setCustomFieldValues(product.customFields ?? {});
  }, [product.ean, product.sku, product.customFields]);

  const persistCustomFieldValue = async (key: string, value: string) => {
    const next = { ...customFieldValues, [key]: value };
    setCustomFieldValues(next);
    await updateProduct({
      productId: product.id,
      updateData: { customFields: { [key]: value } },
    });
  };

  const handleFieldNameChange = async (fieldId: string, oldKey: string, newName: string) => {
    const label = newName.trim();
    const key = label.replace(/\s+/g, '_').replace(/[^A-Za-z0-9_]/g, '');
    if (!label || !key) return;
    if (key === oldKey && label === companyCustomFields.find((field) => field.id === fieldId)?.label) {
      return;
    }
    if (companyCustomFields.some((field) => field.key === key && field.id !== fieldId)) return;

    await updateCustomField({
      customFieldId: fieldId,
      data: { key, label },
    });

    if (key !== oldKey) {
      setCustomFieldValues((prev) => {
        const next = { ...prev };
        next[key] = next[oldKey] ?? '';
        delete next[oldKey];
        return next;
      });
    }
  };

  const handleAddField = async () => {
    const existingKeys = new Set(companyCustomFields.map((field) => field.key));
    let key = 'newField';
    let i = 1;
    while (existingKeys.has(key)) {
      key = `newField${i++}`;
    }

    await createCustomField({
      key,
      label: key,
      applyToAllProducts: true,
    });
  };

  return (
    <Stack direction="column" flex={1} spacing={2}>
      <Typography variant="subtitle2" color="text.primary">Fields</Typography>

      <Stack direction="column" flex={1} spacing={4}>
        <Stack direction="column" spacing={0.5}>
          <Typography variant="body2" color="text.secondary">EAN</Typography>
          <JSEditableInput
            outlined
            label="EAN"
            value={ean}
            onValueChange={(value) => {
              setEan(value);
              updateProduct({ productId: product.id, updateData: { ean: value } });
            }}
            sx={{ width: '100%' }}
          />
        </Stack>

        <Stack direction="column" spacing={0.5}>
          <Typography variant="body2" color="text.secondary">SKU</Typography>
          <JSEditableInput
            outlined
            label="SKU"
            value={sku}
            onValueChange={(value) => {
              setSku(value);
              updateProduct({ productId: product.id, updateData: { sku: value } });
            }}
            sx={{ width: '100%' }}
            type="text"
          />
        </Stack>

        {companyCustomFields.map((field) => (
          <Stack key={field.id} direction="column" spacing={0.5}>
            <Typography variant="body2" color="text.secondary" component="div" sx={{ flex: 1, minWidth: 0 }}>
              <JSEditableInput
                value={field.label}
                onValueChange={(nextName) => handleFieldNameChange(field.id, field.key, nextName)}
                type="text"
                placeholder="Field name"
                sx={{
                  p: 0,
                  minWidth: 0,
                  width: '100%',
                  height: 'auto',
                  border: 'none',
                  backgroundColor: 'transparent',
                  '&:hover': { opacity: 1 },
                }}
              />
            </Typography>
            <JSEditableInput
              outlined
              label={field.label}
              value={customFieldValues[field.key] ?? ''}
              onValueChange={(nextValue) => persistCustomFieldValue(field.key, nextValue)}
              sx={{ width: '100%' }}
              type="text"
              placeholder="Value"
            />
          </Stack>
        ))}
      </Stack>

      <Button variant="text" color="primary" onClick={handleAddField} sx={{ alignSelf: 'flex-start' }}>
        Add Field
      </Button>
    </Stack>
  );
};

export default ProductFields;
